// Column configuration and utility functions
import { formatDateTime } from "./dateUtils";

export const REQUIRED_COLUMN = "stage";

export const ALL_DISPLAY_COLUMNS = [
  "filename",
  "manufacturer",
  "file_id",
  "payer",
  "stage",
  "year",
  "period",
  "num_claims",
  "dollar_amount",
  "num_claims_at_risk",
  "dollar_amount_at_risk",
  "confirmed_amount",
  "invoice_amount",
  "invoice_number",
  "paid_amount",
  "job_id",
  "created_at",
  "content_type",
  "source_type",
];

const DEFAULT_COLUMNS_CIITA = [
  "filename",
  "manufacturer",
  "file_id",
  "payer",
  "stage",
  "year",
  "period",
  "num_claims",
  "dollar_amount",
  "num_claims_at_risk",
  "dollar_amount_at_risk",
  "confirmed_amount",
  "invoice_amount",
  "paid_amount",
];

const DEFAULT_COLUMNS_STANDARD = [
  "filename",
  "file_id",
  "payer",
  "stage",
  "year",
  "period",
  "num_claims",
  "dollar_amount",
  "num_claims_at_risk",
  "dollar_amount_at_risk",
  "confirmed_amount",
  "invoice_amount",
  "paid_amount",
];

export const FILTERABLE_COLUMNS = [
  "stage",
  "payer",
  "manufacturer",
  "year",
  "period",
  "job_id",
  "content_type",
  "source_type",
];

export const STAGE_TO_STATUS_MAP = {
  "00": "Detected",
  "01": "Converted",
  "02": "Cleaned",
  "03": "Standardized",
  "04": "Normalized",
  "05": "Loaded",
  "06": "Deduplicated",
  "07": "Enriched",
  "08": "Processed",
  "09": "Returned",
  10: "Confirmed",
  11: "Invoiced",
  12: "Paid",
};

export const STAGES = {
  CONFIRMED: "10",
  INVOICED: "11",
  PAID: "12",
};

export const COLUMN_TOOLTIPS = {
  filename: "The name of the file at this processing stage.",
  manufacturer: "The manufacturer associated with this file.",
  file_id: "A unique identifier assigned to this file.",
  payer: "The payer associated with this file.",
  stage: "The current processing stage of the file in the pipeline.",
  year: "The year this data is from.",
  period: "The period the data is from.",
  num_claims: "The number of claims included in this file.",
  dollar_amount: "The dollar amount identified in this file.",
  num_claims_at_risk: "The number of claims identified as at risk.",
  dollar_amount_at_risk: "The dollar amount identified as at risk.",
  confirmed_amount: "The confirmed dollar amount after review.",
  invoice_amount: "The amount invoiced to the client.",
  invoice_number: "The invoice number associated with this file.",
  paid_amount: "The amount the client has paid.",
  job_id: "The job ID under which this file was processed.",
  created_at: "The date and time this record was created.",
  content_type: "The type of content (e.g., EDI, CSV, XLSX).",
  source_type: "The source used to input the file (AS2, portal, etc.).",
};

export function getColumnTooltip(key) {
  return COLUMN_TOOLTIPS[key] || formatColumnName(key);
}

// Get visible columns configuration for a user
export function getVisibleColumnsForUser(manufacturer) {
  const isCiita = manufacturer === "ciita";

  const defaultColumns = isCiita
    ? DEFAULT_COLUMNS_CIITA
    : DEFAULT_COLUMNS_STANDARD;
  const allColumns = isCiita
    ? ALL_DISPLAY_COLUMNS
    : ALL_DISPLAY_COLUMNS.filter((col) => col !== "manufacturer");

  return {
    defaultColumns: new Set(defaultColumns),
    allColumns: allColumns,
  };
}

// Format column name for display
export function formatColumnName(key) {
  const customNames = {
    created_at: "Created At",
    job_id: "Job ID",
    file_id: "Unique ID",
    filename: "File Name",
    num_claims: "Number of Claims",
    num_claims_at_risk: "Number of Claims at Risk",
    dollar_amount_at_risk: "Identified Risk Amount",
    confirmed_amount: "Confirmed Amount",
    invoice_amount: "Client Amount to be Invoiced",
    invoice_number: "Invoice Number",
    paid_amount: "Client Paid Amount",
    dollar_amount: "Total Dollar Amount",
    manufacturer: "Manufacturer",
    content_type: "Content Type",
    source_type: "Source Type",
  };

  if (customNames[key]) {
    return customNames[key];
  }

  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Get status text from stage code
export function getStatusFromStage(stageCode) {
  return STAGE_TO_STATUS_MAP[stageCode] || "Pending";
}

// Format currency values
export function formatCurrency(value) {
  if (value === null || value === undefined) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Capitalize first letter
export function capitalizeFirstLetter(string) {
  if (string === null || string === undefined) return "-";
  const str = String(string);
  if (str === "") return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function processCreatedAt(isoString) {
  return {
    raw: isoString, // keep for API filters
    display: formatDateTime(isoString), // human-friendly
  };
}

export function formatNumber(value) {
  if (value === null || value === undefined) return "-";

  const num = Number(value);
  if (isNaN(num)) return "-";

  return num.toLocaleString("en-US");
}

// Format cell value based on column type
export function formatCellValue(value, columnKey) {
  if (columnKey === "stage") {
    return getStatusFromStage(value);
  }

  if (
    columnKey === "manufacturer" ||
    columnKey === "content_type" ||
    columnKey === "source_type"
  ) {
    return capitalizeFirstLetter(value);
  }

  if (columnKey === "num_claims" || columnKey === "num_claims_at_risk") {
    return formatNumber(value);
  }

  if (columnKey === "created_at") {
    if (typeof value === "object" && value !== null && value.display) {
      return value.display;
    }
    return formatDateTime(value);
  }

  if (
    columnKey.endsWith("amount") ||
    columnKey === "dollar_amount" ||
    columnKey === "dollar_amount_at_risk"
  ) {
    return formatCurrency(value);
  }

  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return value;
}
